// Mirrored: web/admin/src/utils ↔ web/concerts/src/utils — keep in sync.
const MEDIA_SRC = /^\/api\/media\/overlays\/[0-9a-fA-F-]{36}\.(jpg|jpeg|png|webp|gif)$/;

const ALLOWED_TAGS = [
  "p",
  "br",
  "h1",
  "h2",
  "h3",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "ul",
  "ol",
  "li",
  "a",
  "img",
  "span",
  "div"
];

// class/style are allowed here because DOMPurify's ALLOWED_ATTR is global.
// The Go bluemonday policy is authoritative and scopes them per element on write.
const ALLOWED_ATTR = ["href", "src", "alt", "target", "rel", "class", "style"];

export function sanitizeOverlayHtml(html, purify) {
  if (!html) {
    return "";
  }
  return purify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ["target"],
    FORBID_TAGS: ["script", "iframe", "object", "embed"],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|\/|#)/i
  });
}

export function isAllowedOverlayImageSrc(src) {
  return typeof src === "string" && MEDIA_SRC.test(src);
}

const ALIGN_VALUE = /^(left|center|right|justify)$/i;
const SIZE_VALUE = /^(18|24|36)px$/i;
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

export function filterOverlayStyle(value) {
  const kept = [];
  for (const decl of String(value || "").split(";")) {
    const idx = decl.indexOf(":");
    if (idx === -1) continue;
    const prop = decl.slice(0, idx).trim().toLowerCase();
    const raw = decl.slice(idx + 1).trim();
    if (prop === "color" && isOverlayColor(raw)) kept.push(`color: ${raw}`);
    else if (prop === "text-align" && ALIGN_VALUE.test(raw)) kept.push(`text-align: ${raw}`);
    else if (prop === "font-size" && SIZE_VALUE.test(raw)) kept.push(`font-size: ${raw}`);
  }
  return kept.join("; ");
}

function isOverlayColor(raw) {
  if (HEX_COLOR.test(raw)) return true;
  const rgb = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i.exec(raw);
  const rgba =
    /^rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(0|1|0?\.\d+)\s*\)$/i.exec(raw);
  const match = rgb || rgba;
  if (!match) return false;
  return [match[1], match[2], match[3]].every((channel) => Number(channel) <= 255);
}

export function createOverlayPurifier(factory) {
  const purify = factory(window);
  purify.addHook("uponSanitizeAttribute", (_node, data) => {
    if (data.attrName === "src" && !isAllowedOverlayImageSrc(data.attrValue)) {
      data.keepAttr = false;
    }
    if (data.attrName === "style") {
      const next = filterOverlayStyle(data.attrValue);
      if (!next) data.keepAttr = false;
      else data.attrValue = next;
    }
  });
  return purify;
}
